import {Injectable} from '@angular/core';
import {NavigationStart, Router} from '@angular/router';
import {Observable, Subject} from 'rxjs';
import {Alert, AlertType} from './alert.model';
import {ApplicationErreur} from '../../exception/ApplicationErreur';
import {TranslateService} from '@ngx-translate/core';

@Injectable({
		providedIn: 'root'
	}
)
export class AlertService {
	private subject = new Subject<Alert>();
	private keepAfterRouteChange = false;

	constructor(
		private router: Router,
		private translateService: TranslateService
	) {
		// clear alert messages on route change unless 'keepAfterRouteChange' flag is true
		router.events.subscribe(event => {
			if (event instanceof NavigationStart) {
				if (this.keepAfterRouteChange) {
					// only keep for a single route change
					this.keepAfterRouteChange = false;
				}
				else {
					// clear alert messages
					this.clear(undefined);
				}
			}
		});
	}

	getAlert(): Observable<any> {
		return this.subject.asObservable();
	}

	success(message: string, nom: string = 'main', msgParams?: any) {
		this.alert(AlertType.Success, message, true, false, nom, msgParams);
	}

	error(erreur: ApplicationErreur, nom: string = 'main', msgParams?: any) {
		this.alert(AlertType.Error, this.formaterErreur(erreur), true, false, nom, msgParams);
	}

	errorWithButton(erreur: ApplicationErreur, nom: string = 'main', clickFunction: Function, args: any[], msgParams?: any) {
		this.alertWithBouton(AlertType.Error, this.formaterErreur(erreur), true, false, nom, clickFunction, args, msgParams);
	}

	errorWith2Buttons(erreur: ApplicationErreur, nom: string = 'main', clickFunction: Function, clickFunctionBouton2: Function, args: any[], argsBouton2: any[], msgParams?: any) {
		this.alertWith2Boutons(AlertType.Error, this.formaterErreur(erreur), true, false, nom, clickFunction, clickFunctionBouton2, args, argsBouton2, msgParams);
	}

	errorSansCode(message: string, nom: string = 'main', msgParams?: any) {
		this.alert(AlertType.Error, message, true, false, nom, msgParams);
	}

	info(message: string, nom: string = 'main', msgParams?: any) {
		this.alert(AlertType.Info, message, true, false, nom, msgParams);
	}

	infoWithButton(message: string, nom: string = 'main', clickFunction: Function, args: any[], msgParams?: any) {
		this.alertWithBouton(AlertType.Info, message, true, false, nom, clickFunction, args, msgParams);
	}

	infoWith2Buttons(message: string, nom: string = 'main', clickFunction: Function, clickFunctionBouton2: Function, args: any[], argsBouton2: any[], msgParams?: any) {
		this.alertWith2Boutons(AlertType.Info, message, true, false, nom, clickFunction, clickFunctionBouton2, args, argsBouton2, msgParams);
	}

	warn(message: string, nom: string = 'main', msgParams?: any) {
		this.alert(AlertType.Warning, message, true, false, nom, msgParams);
	}

	warnSansCode(message: string, nom: string = 'main', msgParams?: any) {
		this.alert(AlertType.Warning, message, true, false, nom, msgParams);
	}

	warnMultiple(error: ApplicationErreur, afficherCodePrincipalErreur: boolean, remplacementCodeErreurSecondaire: string, nom: string = 'main', msgParams?: any) {
		if (!afficherCodePrincipalErreur) {
			error.code = undefined;
		}
		let erreursFormattees = error.message;
		if (remplacementCodeErreurSecondaire) {
			let erreurs = error.message.split('\n');
			erreurs = erreurs.map(erreur => {
				return erreur.replace(/[[0-9]*]/, remplacementCodeErreurSecondaire);
			});
			erreursFormattees = erreurs.join('\n');
		}

		error.message = erreursFormattees.replace(/(\r\n|\n|\r)/gm, '<br>');
		this.alert(AlertType.Warning, this.formaterErreur(error), true, false, nom, msgParams);
	}

	warnWithBouton(message: string, nom: string = 'main', clickFunction: Function, args: any[], msgParams?: any) {
		this.alertWithBouton(AlertType.Warning, message, true, false, nom, clickFunction, args, msgParams);
	}

	warnWith2Boutons(message: string, nom: string = 'main', clickFunction: Function, clickFunctionBouton2: Function, args: any[], argsBouton2: any[], msgParams?: any) {
		this.alertWith2Boutons(AlertType.Warning, message, true, false, nom, clickFunction, clickFunctionBouton2, args, argsBouton2, msgParams);
	}

	alert(type: AlertType, message: string, keep = true, keepAfterRouteChange = false, nom: string, msgParams?: any) {
		this.keepAfterRouteChange = keepAfterRouteChange;
		this.subject.next(<Alert>{type: type, message: message, keep: keep, nom: nom, msgParams: msgParams});
	}

	alertWithBouton(type: AlertType, message: string, keep = true, keepAfterRouteChange = false, nom: string, clickFunction: Function, args: any[], msgParams?: any) {
		this.keepAfterRouteChange = keepAfterRouteChange;
		this.subject.next(<Alert>{type: type, message: message, keep: keep, nom: nom, onClickFunction: clickFunction, functionArguments: args, msgParams: msgParams});
	}

	alertWith2Boutons(type: AlertType, message: string, keep = true, keepAfterRouteChange = false, nom: string, clickFunction: Function, clickFunctionBouton2: Function, args: any[],
					  argsBouton2: any[], msgParams?: any) {
		this.keepAfterRouteChange = keepAfterRouteChange;
		this.subject.next(<Alert>{type: type, message: message, keep: keep, nom: nom, onClickFunction: clickFunction, onClickFunctionBouton2: clickFunctionBouton2, functionArguments: args,
			functionArgumentsBouton2: argsBouton2, msgParams: msgParams});
	}

	clear(nom: string = 'main') {
		// clear alerts
		this.subject.next(<Alert>{nom: nom, clear: true});
	}

	formaterErreur(erreur: ApplicationErreur): string {
		let messageFormatte = '';
		if (erreur.code) {
			messageFormatte = '[' + erreur.code + '] ';
			if (erreur.message) {
				messageFormatte += erreur.message;
			}
			else if (erreur.countErreurs) {
				// Les erreurs automatiques remontées par les contrôles sur les VOS (comme @DecimalMin) sont envoyées sous ce format.
				Object.values(erreur.erreurs).forEach(erreurAFormatte => messageFormatte += erreurAFormatte.message + '<br>');
			}
		}
		else {
			// traite le cas où le message ne contient pas de code d'erreur
			if (erreur.message) {
				messageFormatte = erreur.message;
			}
			else {
				// erreur vraiment inconnue
				messageFormatte = this.translateService.instant('MSG_ERREUR_NON_IDENTIFIEE');
			}
		}

		return messageFormatte;
	}
}
